import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { Fade, Grid, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import BusinessIcon from '@mui/icons-material/Business';
import { useSnackbar } from 'notistack';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import { blue, red } from '@mui/material/colors';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import ProcessGuide from '../../components/orden/ProcessGuide';

const CHIP_DEFS = [
    { key: 'todos', label: 'Todos', color: 'primary' },
    { key: 'publicos', label: 'Públicos', color: 'info' },
    { key: 'privados', label: 'Privados', color: 'secondary' },
    { key: 'activos', label: 'Activos', color: 'success' },
    { key: 'inactivos', label: 'Inactivos', color: 'default' },
];

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [selectedChip, setSelectedChip] = useState('todos');
    const [counts, setCounts] = useState({});
    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const chipItems = useMemo(
        () => CHIP_DEFS.map((c) => ({ ...c, count: counts[c.key] != null ? counts[c.key] : 0 })),
        [counts]
    );

    const onChipChange = useCallback((key) => {
        setSelectedChip(key);
        setTableMainNeedUpdate(Date.now());
    }, []);

    const onNuevo = useCallback(() => {
        globalsContext.setIdEdit(false);
        navigate('/establecimiento/nuevo');
    }, [globalsContext, navigate]);

    const onEditar = useCallback((row) => {
        globalsContext.setIdEdit(row.id);
        navigate('/establecimiento/editar');
    }, [globalsContext, navigate]);

    const onEliminar = useCallback((row) => {
        alertModal.showModal(
            'Eliminar establecimiento',
            'El establecimiento "' + (row.nombre || '') + '" será eliminado. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await deleteData(`establecimientos/${row.id}`, usuario.token);
                if (response.error === '') {
                    enqueueSnackbar('Establecimiento eliminado correctamente', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            }
        );
    }, [usuario.token, alertModal, loadingModal, enqueueSnackbar]);

    const tableMain = useMemo(() => ({
        title: 'Establecimientos',
        idName: 'id',
        defaultOrder: 'asc',
        headers: [
            { id: 'nombre', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
            {
                id: 'tipo',
                label: 'Tipo',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip
                        size="small"
                        color={row.tipo === 'publico' ? 'info' : 'secondary'}
                        label={row.tipo === 'publico' ? 'Público' : 'Privado'}
                    />
                ),
            },
            {
                id: 'activo',
                label: 'Estado',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip size="small" color={row.activo ? 'success' : 'default'} label={row.activo ? 'Activo' : 'Inactivo'} />
                ),
            },
            { id: 'localidad_nombre', label: 'Localidad', dataType: DataType.TEXT, sortable: false },
            { id: 'provincia_nombre', label: 'Provincia', dataType: DataType.TEXT, sortable: false },
            { id: 'texto_display', label: 'Dirección', dataType: DataType.TEXT, sortable: false },
            { id: 'practicas_count', label: 'Prácticas', dataType: DataType.NUMBER, sortable: false },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, _sf, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const params = new URLSearchParams();
            params.append('per_page', String(rowsPerPage));
            params.append('page', String(page + 1));
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);
            if (selectedChip && selectedChip !== 'todos') params.append('chip', selectedChip);

            const response = await getDataFull(`establecimientos?${params.toString()}`, usuario.token);
            if (response.error === '') {
                pageUpdate(response.meta?.page ? response.meta.page - 1 : page);
                totalUpdate(response.meta?.total || 0);
                if (filterListUpdate) filterListUpdate([]);
                if (response.counts) setCounts(response.counts);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: [],
        rowActions: (row) => [
            {
                label: 'Editar',
                secondary: 'Modificar datos del establecimiento',
                icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                itemColor: blue[600],
                action: () => onEditar(row),
            },
            {
                label: 'Eliminar',
                secondary: 'Eliminar este establecimiento',
                icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                itemColor: red[600],
                dividerBefore: true,
                action: () => onEliminar(row),
            },
        ],
    }), [usuario.token, selectedChip, onEditar, onEliminar, enqueueSnackbar]);

    return (
        <Layout nombrePagina="Establecimientos">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <ListadoOperativoBanner
                        title="Establecimientos"
                        description="Gestiona centros públicos y privados, su localidad y las prácticas que ofrecen. El tipo define si pueden cargar u solo aceptar órdenes."
                        icon={BusinessIcon}
                        accentColor="secondary"
                        headerAction={(
                            <Button startIcon={<AddCircleOutlineRoundedIcon />} onClick={onNuevo}>
                                Nuevo establecimiento
                            </Button>
                        )}
                        chips={chipItems}
                        selectedChip={selectedChip}
                        onChipChange={onChipChange}
                    />
                    <ProcessGuide
                        title="Guía rápida"
                        steps={[
                            'Los establecimientos públicos pueden cargar órdenes; los privados solo responden y cierran.',
                            'La localidad se usa para la autoasignación de pedidos.',
                            'Vincule las prácticas que el centro puede realizar.',
                        ]}
                    />
                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={10}
                        needUpdate={tableMainNeedUpdate}
                        hideFilters
                        onRowClick={(row) => onEditar(row)}
                    />
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
